import axios from 'axios';
import { Area, AvailableTime, GeoLocation, Marker, MarkerDetails, TreatmentFilter } from './models';
// import markersMockup from '@/views/search/components/search.markers.mockup.json';
// import detailsMockup from '@/views/search/components/search.details.mockup.json';

const service = axios.create({
	baseURL: '/api/ajax/SearchApi/'
});

const appointmentService = axios.create({
	baseURL: '/api/ajax/SearchAppointmentApi/'
});

const googleService = axios.create({
	baseURL: 'https://maps.googleapis.com/maps/api/geocode/'
});

const cacheService = axios.create({
	baseURL: '/api/ajax/SalonCacheApi/'
});

export default {
	/**
	 * Search for Google Maps locations by provided string.
	 * @param {String} searchString - The string to use for the location search.
	 * @returns Array of GeoLocations
	 */
	async GetLocations(searchString = '') {
		const result = await axios.post('/api/ajax/SearchApi/SearchByString', {
			hideSalons: false,
			industry: '0',
			istpn: false,
			searchString: searchString,
			tpnChainId: 0,
			tpnSiteId: 0
		});
		if (result.data && result.data.ResponseCode == 200 && result.data.Children.length) {
			let geoLocations = result.data.Children
				// Reduce and flat locations
				.reduce((acc, cur) => {
					acc.push(cur.Children);
					return acc;
				}, [])
				.flat()

				// Map locations as GeoLocations
				.map(
					geoLocation =>
						new GeoLocation(
							// Camel casing all keys before passing to constructor.
							Object.assign(
								{},
								...Array.from(
									Object.entries(geoLocation).map(([key, value]) => [key.substr(0, 1).toLowerCase() + key.substr(1), value]),
									([key, value]) => ({ [key]: value })
								)
							)
						)
				);

			geoLocations.sort(GeoLocation.sort);
			return geoLocations;
		}

		return [];
	},

	/**
	 * Get google maps location from address.
	 * @param {String} address - The address used to query google maps.
	 */
	async GetLocationFromAddress(address) {
		const result = await googleService.get(`https://maps.googleapis.com/maps/api/geocode/json?address=${address}&language=no&components=no&region=no&key=${window.CONFIG.google.geoApiKey}&oe=utf-8`);
		if (result.data && result.data.status === 'OK' && Array.isArray(result.data.results) && result.data.results.length) {
			return result.data.results[0];
		}

		return false;
	},

	/**
	 * Get google maps location from latitude and longitude.
	 * @param {Number} latitude - Latitude
	 * @param {Number} longitude - Longitude
	 */
	async GetLocationFromLatLng(latitude, longitude) {
		const result = await googleService.get(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&language=no&components=no&region=no&key=${window.CONFIG.google.geoApiKey}&oe=utf-8`);
		if (result.data && result.data.status === 'OK' && Array.isArray(result.data.results) && result.data.results.length) {
			return result.data.results[0];
		}

		return false;
	},

	/**
	 * Get locations for all sites
	 * @param {Number[]=} siteIds - Array of siteIds to filter for. If empty or undefined, returns for all sites.
	 * @todo Mockup method. Using Jon's local machine as service.
	 */
	async GetLocationsForSites(/* siteIds = [] */) {
		// return markersMockup.map(m => new Marker(m));

		const result = await axios.get('/api/ajax/SalonCacheApi/SalonBasic');
		if (result.data && result.data.length) {
			return result.data.map(m => new Marker(m));
		}

		return [];
	},

	/**
	 * Get detailed information for sites
	 * @param {Number[]=} siteIds - Array of siteIds to filter for. If empty or undefined, returns for all sites.
	 * @todo Mockup method. Using Jon's local machine as service.
	 */
	async GetDetailsForSites(siteIds = []) {
		// return detailsMockup.map(m => new MarkerDetails(m));

		const result = await axios.post('/api/ajax/SalonCacheApi/SalonDetailed', siteIds || []);
		if (result.data && result.data.length) {
			return result.data.map(m => new MarkerDetails(m));
		}

		return [];
	},

	/**
	 * Get treatment and area
	 * @param {String} treatment - The treatment name
	 * @param {String} area - The area name
	 */
	async GetTreatmentAndArea(treatment = '', area = '') {
		const result = await axios.post('/api/ajax/SearchApi/GetTreatmentAndArea', { treatment, area });
		if (result.data) {
			return {
				treatment: result.data.Treatment && new TreatmentFilter(result.data.Treatment),
				area: result.data.Area && new Area(result.data.Area)
			};
		}

		return {};
	},

	/**
	 * Get available time for selected siteIds.
	 * @param {Number[]} siteIds - The siteIds to collect available time for.
	 * @param {Date} date - The date to check availability.
	 * @param {Number} treatmentId - Filter availability on treatment.
	 * @returns {Promise<AvailableTime[]>} Available time table for selected sites.
	 */
	async GetAvailableTimeBySalons(siteIds = [], date = new Date(), treatmentId = 0) {
		const result = await axios.post('/api/ajax/SearchAppointmentApi/GetAvailableTimeBySalons', { siteIds, year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate(), odinTreatmentCategory: treatmentId });
		if (Object(Object(result.data).AvailableTimeInSalonList).length) {
			return result.data.AvailableTimeInSalonList.map(a => new AvailableTime(a));
		}

		return [];
	}
};
